/**
 * Ce que l'interface propose — adossé aux permissions de l'API.
 *
 * Chaque capacité d'écran correspond à une permission renvoyée dans
 * `abilities` par `/auth/login` et `/me`. Rien n'est déduit du nom du rôle :
 * le superadmin en crée à volonté, et un rôle sur mesure n'a aucun nom connu
 * à l'avance.
 *
 * L'API reste l'autorité : elle répond 403 quoi qu'affiche l'interface. Cette
 * table sert uniquement à ne pas proposer un bouton qui échouerait. Toute
 * divergence avec les routes est un défaut à corriger ICI, pas côté back.
 */

export type Capability =
  | 'stations.manage'
  | 'users.manage'
  | 'modules.manage'
  | 'roles.manage'
  | 'audit.read'
  | 'tanks.gauge'
  | 'assets.manage'
  | 'prices.manage'
  | 'shifts.open'
  | 'shifts.close'
  | 'shifts.approve'
  | 'procurement.write'
  | 'customers.read'
  | 'customers.write'
  | 'payments.record'
  | 'vouchers.read'
  | 'vouchers.manage'
  | 'reports.view'
  | 'reports.export'
  | 'shop.view'
  | 'shop.manage'
  | 'shop.sell'
  | 'shop.count'
  | 'anomalies.read'
  | 'anomalies.resolve'
  | 'maintenance.write'
  | 'inspections.read'
  | 'inspections.write'

/** Permission exigée par l'API pour chaque capacité d'écran. */
const REQUIRED: Record<Capability, string> = {
  'stations.manage': 'stations.manage',
  'users.manage': 'users.manage',
  'modules.manage': 'modules.manage',
  'roles.manage': 'roles.manage',
  'audit.read': 'audit-logs.view',
  // Le pompiste ne jauge JAMAIS : il déclare l'index, un autre mesure la cuve.
  'tanks.gauge': 'tank-gaugings.create',
  // Déclarer du matériel ou changer un prix engage tout le réseau : ce sont
  // des actes de configuration, distincts de l'exploitation quotidienne.
  'assets.manage': 'station-assets.manage',
  'prices.manage': 'fuel-products.manage',
  'shifts.open': 'work-shifts.operate',
  'shifts.close': 'work-shifts.operate',
  'shifts.approve': 'work-shifts.approve',
  'procurement.write': 'procurement.manage',
  'customers.read': 'credit.view',
  'customers.write': 'credit.manage',
  'payments.record': 'payments.record',
  'vouchers.read': 'vouchers.view',
  'vouchers.manage': 'vouchers.manage',
  // Les totaux du réseau : sommés par le serveur, jamais recalculés ici.
  'reports.view': 'reports.view',
  // Exporter fait sortir la donnée du produit : droit distinct de la lecture.
  'reports.export': 'reports.export',
  // Quatre gestes distincts : celui qui vend ne redresse pas le stock qu'il
  // aurait vidé, et ne le compte pas seul.
  'shop.view': 'shop.view',
  'shop.manage': 'shop.manage',
  'shop.sell': 'shop.sell',
  'shop.count': 'shop.count',
  'anomalies.read': 'anomalies.view',
  'anomalies.resolve': 'anomalies.resolve',
  'maintenance.write': 'maintenance.manage',
  'inspections.read': 'inspections.view',
  'inspections.write': 'inspections.manage',
}

export function abilityCan(abilities: readonly string[], capability: Capability): boolean {
  return abilities.includes(REQUIRED[capability])
}
